import { useMemo, useState } from 'react';
import {
  usePersonalInformation,
  useUpdateUserProfile,
} from 'hooks/use-personal-information.hook';
import { useCommunicationPreferenceManager } from 'hooks/communications/use-communication-preference-manager.hook';
import { useProductSegmentation } from 'hooks/use-product-segmentation.hook';
import { useCommunicationPreferenceMapper } from 'hooks/communications/use-communication-preference-mapper.hook';
import { useErrorHandler } from 'hooks/use-error-handler.hook';
import { useProfileToast } from 'hooks/use-profile-toast.hook';
import {
  CommunicationOption,
  ApiGetData,
  ApiPatchData,
} from 'models/profile-settings.model';
import { sendAccountSetupButtonClickedEvent } from 'utils/analytics-events';

interface FormData {
  preferredFirstName: string;
  communicationOptions: CommunicationOption[];
}

interface UseAccountSetupFormProps {
  onComplete: () => void;
  onSubmitError?: (error: Error) => void;
}

interface UseAccountSetupFormReturn {
  formData: FormData;
  initialOptions: CommunicationOption[];
  isFetching: boolean;
  fetchErrorCode: number | undefined;
  isUpdating: boolean;
  isCompleting: boolean;
  handleInputChange: (field: string, value: string | boolean) => void;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
}

export const useAccountSetupForm = ({
  onComplete,
  onSubmitError,
}: UseAccountSetupFormProps): UseAccountSetupFormReturn => {
  const { data } = usePersonalInformation();
  const { showPaperlessSetup } = useProductSegmentation();
  const { updateUserProfile } = useUpdateUserProfile({});
  const {
    preferences,
    isFetching,
    fetchErrorCode,
    isUpdating,
    updatePreferences,
  } = useCommunicationPreferenceManager({
    showNotification: false,
  });
  const { mapApiToOptions, mapOptionsToApi } =
    useCommunicationPreferenceMapper();
  const { handleAccountSetupError } = useErrorHandler();
  const { onSuccess } = useProfileToast();
  const [isCompleting, setIsCompleting] = useState(false);

  const initialOptions = useMemo(
    () =>
      mapApiToOptions(
        preferences as ApiGetData | undefined,
        showPaperlessSetup,
      ),
    [preferences, showPaperlessSetup, mapApiToOptions],
  );

  const [formData, setFormData] = useState<FormData>({
    preferredFirstName: data?.userProfile?.preferredFirstName ?? '',
    communicationOptions: initialOptions,
  });

  const handleInputChange = (field: string, value: string | boolean): void => {
    if (formData.communicationOptions.some((opt) => opt.labelId === field)) {
      setFormData((prev) => {
        const newOptions = prev.communicationOptions.map((opt) =>
          opt.labelId === field ? { ...opt, value: value as boolean } : opt,
        );
        return {
          ...prev,
          communicationOptions: newOptions,
        };
      });
    } else if (field === 'STR_PREFERRED_FIRST_NAME_OPTIONAL') {
      setFormData((prev) => ({
        ...prev,
        preferredFirstName: value as string,
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    sendAccountSetupButtonClickedEvent('continue');

    setIsCompleting(true);

    try {
      const oppositeInitialOptions: CommunicationOption[] =
        formData.communicationOptions.map((option) => ({
          ...option,
          value: !option.value,
        }));
      const payload: ApiPatchData = mapOptionsToApi(
        formData.communicationOptions,
        oppositeInitialOptions,
        data?.email,
      );

      await updatePreferences(payload);

      await updateUserProfile({
        preferred_first_name: formData.preferredFirstName,
        custom_fields: [{ name: 'account_setup_completed', value: 'true' }],
      });

      onSuccess('STR_PROFILE_SETTING_SUCCESS_MSG');

      onComplete();
    } catch (error: any) {
      setIsCompleting(false);

      handleAccountSetupError(error);
      if (onSubmitError) {
        onSubmitError(error as Error);
      }
    }
  };

  return {
    formData,
    initialOptions,
    isFetching,
    fetchErrorCode,
    isUpdating,
    isCompleting,
    handleInputChange,
    handleSubmit,
  };
};
